import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTeam } from 'shared/context/teamContext';
import axiosInstance from 'shared/services/api/axiosInstance';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import { settingsInputClass } from 'features/settings/components/settingsTheme';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';
const AUTOSAVE_MS = 650;

// Shared with every other settings field rather than a local near-copy.
const INPUT_CLS = settingsInputClass;

const cellButtonClass =
  'group relative w-full max-w-full text-left rounded-md px-1 py-0.5 -mx-1 transition-colors min-h-[1.5rem] flex items-center'
  + ' focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

function normalizeTags(tags = []) {
  if (!Array.isArray(tags)) return [];
  return [...new Set(tags.map((t) => (t || '').trim()).filter(Boolean))];
}

function stableEventKey(event) {
  return `${event.event_type}:${event.event_name}`;
}

/**
 * NB: `description` is carried through the draft and sent on every save even
 * though this table never renders it. That is deliberate — the PUT is an upsert
 * that does `SET description = EXCLUDED.description` with no COALESCE, so
 * omitting the field would null out whatever the team had stored. Don't
 * "clean this up" without changing the server to patch instead of replace.
 */
function toDraft(event) {
  return {
    display_name: event.display_name || '',
    description: event.description || '',
    category: event.category || '',
    tags: normalizeTags(event.tags || [])
  };
}

function isDirtyDraft(draft, base) {
  return (
    draft.display_name !== base.display_name ||
    draft.description !== base.description ||
    draft.category !== base.category ||
    JSON.stringify(normalizeTags(draft.tags)) !== JSON.stringify(normalizeTags(base.tags))
  );
}

/** Single-line inline field: click to edit, debounced save, empty hover hint */
function InlineTextCell({ value, emptyHint, onCommit, ariaLabel, saving, saveError, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const debounceRef = useRef(null);
  const lastSavedRef = useRef('');
  const inputRef = useRef(null);

  const trimmed = (value || '').trim();
  const hasValue = trimmed.length > 0;

  useEffect(() => {
    if (!editing) lastSavedRef.current = value || '';
  }, [value, editing]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const runCommit = useCallback(
    async (next) => {
      if (next === lastSavedRef.current) return true;
      const ok = await onCommit(next);
      if (ok) lastSavedRef.current = next;
      return ok;
    },
    [onCommit]
  );

  const scheduleCommit = useCallback(
    (next) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runCommit(next), AUTOSAVE_MS);
    },
    [runCommit]
  );

  const startEdit = () => {
    const d = value || '';
    setDraft(d);
    lastSavedRef.current = d;
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const handleBlur = async () => {
    clearTimeout(debounceRef.current);
    const v = draft;
    if (v !== lastSavedRef.current) await runCommit(v);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className={`min-w-[8rem] max-w-xs ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => {
            const v = e.target.value;
            setDraft(v);
            scheduleCommit(v);
          }}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(lastSavedRef.current);
              setEditing(false);
            }
          }}
          className={INPUT_CLS}
          placeholder={emptyHint}
          aria-label={ariaLabel}
        />
        {(saving || saveError) && (
          <div className={`mt-0.5 text-[11px] ${saveError ? 'text-danger' : 'text-[color:var(--text-muted)]'}`}>
            {saveError || 'Saving…'}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      className={`${cellButtonClass} text-[color:var(--text-muted)] ${className}`}
      title={hasValue ? 'Click to edit' : emptyHint}
      aria-label={ariaLabel}
    >
      {hasValue ? (
        <span className="block truncate w-full rounded px-1 py-0.5 group-hover:bg-surface-hover/80 group-hover:text-[color:var(--text-primary)] text-[color:var(--text-primary)]">
          {trimmed}
        </span>
      ) : (
        <span
          className="pointer-events-none block min-h-[1.25rem] truncate rounded px-1 py-0.5 text-[12px] text-[color:var(--text-disabled)] transition-colors group-hover:bg-surface-hover/80 group-hover:text-[color:var(--text-muted)]"
          aria-hidden
        >
          {emptyHint}
        </span>
      )}
    </button>
  );
}

const CipherEvents = () => {
  const { selectedTeam } = useTeam();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftByKey, setDraftByKey] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [cellErrorKey, setCellErrorKey] = useState(null);
  const [tagOptions, setTagOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [openCategoryKey, setOpenCategoryKey] = useState(null);
  const [openTagMenuKey, setOpenTagMenuKey] = useState(null);
  const [isCreatingCategoryByKey, setIsCreatingCategoryByKey] = useState({});
  const [newCategoryByKey, setNewCategoryByKey] = useState({});
  const [tagInputByKey, setTagInputByKey] = useState({});
  const [categoryMenuPos, setCategoryMenuPos] = useState(null);
  const [tagMenuPos, setTagMenuPos] = useState(null);

  const eventsRef = useRef(events);
  const draftMapRef = useRef({});
  const saveTimersRef = useRef({});

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    draftMapRef.current = draftByKey;
  }, [draftByKey]);

  useEffect(() => {
    if (selectedTeam?._id) fetchEventsAndDefinitions();
  }, [selectedTeam]);

  const fetchEventsAndDefinitions = async () => {
    if (!selectedTeam?._id) return;

    setLoading(true);
    setError(null);
    try {
      const [summariesResponse, definitionsResponse, optionsResponse] = await Promise.all([
        axiosInstance.get('/events/summaries', { params: { team_id: selectedTeam._id } }),
        axiosInstance.get('/events/definitions', { params: { team_id: selectedTeam._id } }),
        axiosInstance.get('/events/definitions/options', { params: { team_id: selectedTeam._id } })
      ]);

      if (!summariesResponse.data?.success) {
        setError('Failed to fetch events');
        return;
      }

      // Only the summaries response used to be checked. If /definitions failed
      // but resolved, every display name / category / tag silently fell back to
      // empty — indistinguishable from "not set yet", and an edit would then
      // overwrite good data with blanks.
      if (!definitionsResponse.data?.definitions) {
        setError('Could not load event details. Reload before editing: saving now could overwrite existing values.');
        return;
      }

      const summaries = summariesResponse.data?.summaries || [];
      const definitions = definitionsResponse.data?.definitions || [];
      const definitionMap = new Map(definitions.map((d) => [d.event_name, d]));

      const customSummaries = summaries.filter((evt) => evt.event_type === 'custom');

      const merged = customSummaries.map((evt) => {
        const def = definitionMap.get(evt.event_name);
        return {
          ...evt,
          display_name: def?.display_name || '',
          description: def?.description || '',
          tags: normalizeTags(def?.tags || []),
          category: def?.category || ''
        };
      });

      setEvents(merged);
      setDraftByKey({});
      draftMapRef.current = {};
      setTagOptions(normalizeTags(optionsResponse.data?.tags || []));
      setCategoryOptions((optionsResponse.data?.categories || []).filter(Boolean));
    } catch (err) {
      console.error('Error fetching events configuration data:', err);
      setError(err.response?.data?.error || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  /** Merge partial draft; keep draftMapRef in sync for timers. */
  const patchDraft = useCallback((event, partial) => {
    const key = stableEventKey(event);
    const evtRow = eventsRef.current.find((e) => stableEventKey(e) === key) || event;
    setDraftByKey((prev) => {
      const nextDraft = { ...(prev[key] || toDraft(evtRow)), ...partial };
      draftMapRef.current = { ...draftMapRef.current, [key]: nextDraft };
      return { ...prev, [key]: nextDraft };
    });
  }, []);

  const performSave = useCallback(
    async (key, draft) => {
      if (!selectedTeam?._id) return false;
      const event = eventsRef.current.find((e) => stableEventKey(e) === key);
      if (!event) return false;
      const base = toDraft(event);
      if (!isDirtyDraft(draft, base)) return true;

      setSavingKey(key);
      setCellErrorKey(null);
      try {
        const payload = {
          team_id: selectedTeam._id,
          event_type: event.event_type,
          display_name: draft.display_name.trim() || null,
          description: draft.description.trim() || null,
          category: draft.category.trim() || null,
          tags: normalizeTags(draft.tags)
        };

        const response = await axiosInstance.put(
          `/events/definitions/${encodeURIComponent(event.event_name)}`,
          payload
        );
        const updated = response.data?.definition;
        if (!response.data?.success || !updated) {
          throw new Error('Failed to save event configuration');
        }

        const savedDraft = {
          display_name: updated.display_name || '',
          description: updated.description || '',
          category: updated.category || '',
          tags: normalizeTags(updated.tags || [])
        };

        setEvents((prev) =>
          prev.map((evt) => {
            if (stableEventKey(evt) !== key) return evt;
            return {
              ...evt,
              ...savedDraft
            };
          })
        );
        setDraftByKey((prev) => {
          const next = { ...prev, [key]: savedDraft };
          draftMapRef.current = { ...draftMapRef.current, [key]: savedDraft };
          return next;
        });

        if (updated.category) {
          setCategoryOptions((prev) =>
            prev.includes(updated.category)
              ? prev
              : [...prev, updated.category].sort((a, b) => a.localeCompare(b))
          );
        }
        const nextTags = normalizeTags(updated.tags || []);
        if (nextTags.length) {
          setTagOptions((prev) => normalizeTags([...prev, ...nextTags]));
        }
        setError(null);
        return true;
      } catch (err) {
        console.error('Failed to save event definition:', err);
        setCellErrorKey(key);
        setError(err.response?.data?.error || err.message || 'Failed to save event configuration');
        return false;
      } finally {
        setSavingKey(null);
      }
    },
    [selectedTeam]
  );

  const scheduleAutosave = useCallback(
    (key) => {
      if (saveTimersRef.current[key]) clearTimeout(saveTimersRef.current[key]);
      saveTimersRef.current[key] = setTimeout(() => {
        const evt = eventsRef.current.find((e) => stableEventKey(e) === key);
        if (!evt) return;
        const draft = draftMapRef.current[key] || toDraft(evt);
        performSave(key, draft);
        delete saveTimersRef.current[key];
      }, AUTOSAVE_MS);
    },
    [performSave]
  );

  const cancelScheduledSave = useCallback((key) => {
    if (saveTimersRef.current[key]) {
      clearTimeout(saveTimersRef.current[key]);
      delete saveTimersRef.current[key];
    }
  }, []);

  const commitDisplayName = useCallback(
    async (event, displayName) => {
      const key = stableEventKey(event);
      cancelScheduledSave(key);
      const evtRow = eventsRef.current.find((e) => stableEventKey(e) === key) || event;
      const draft = { ...(draftMapRef.current[key] || toDraft(evtRow)), display_name: displayName };
      draftMapRef.current = { ...draftMapRef.current, [key]: draft };
      setDraftByKey((prev) => ({ ...prev, [key]: draft }));
      return performSave(key, draft);
    },
    [performSave, cancelScheduledSave]
  );

  const addTag = useCallback(
    (event, value) => {
      const key = stableEventKey(event);
      const nextTag = (value || '').trim();
      if (!nextTag) return;
      const evtRow = eventsRef.current.find((e) => stableEventKey(e) === key) || event;
      const currentTags = normalizeTags(draftMapRef.current[key]?.tags || toDraft(evtRow).tags);
      if (currentTags.includes(nextTag)) return;
      patchDraft(event, { tags: [...currentTags, nextTag] });
      setTagInputByKey((prev) => ({ ...prev, [key]: '' }));
      scheduleAutosave(key);
    },
    [patchDraft, scheduleAutosave]
  );

  const removeTag = useCallback(
    (event, tag) => {
      const key = stableEventKey(event);
      const evtRow = eventsRef.current.find((e) => stableEventKey(e) === key) || event;
      const currentTags = normalizeTags(draftMapRef.current[key]?.tags || toDraft(evtRow).tags);
      patchDraft(event, { tags: currentTags.filter((t) => t !== tag) });
      scheduleAutosave(key);
    },
    [patchDraft, scheduleAutosave]
  );

  const selectCategory = useCallback(
    (event, cat) => {
      const key = stableEventKey(event);
      cancelScheduledSave(key);
      const evtRow = eventsRef.current.find((e) => stableEventKey(e) === key) || event;
      const draft = { ...(draftMapRef.current[key] || toDraft(evtRow)), category: cat };
      draftMapRef.current = { ...draftMapRef.current, [key]: draft };
      setDraftByKey((prev) => ({ ...prev, [key]: draft }));
      setOpenCategoryKey(null);
      setCategoryMenuPos(null);
      setIsCreatingCategoryByKey((prev) => ({ ...prev, [key]: false }));
      performSave(key, draft);
    },
    [performSave, cancelScheduledSave]
  );

  const getAvailableTagOptions = useCallback(
    (key) => {
      const event = eventsRef.current.find((e) => stableEventKey(e) === key);
      if (!event) return [];
      const selected = new Set(normalizeTags(draftMapRef.current[key]?.tags || toDraft(event).tags));
      const query = (tagInputByKey[key] || '').trim().toLowerCase();
      return tagOptions
        .filter((tag) => !selected.has(tag))
        .filter((tag) => (query ? tag.toLowerCase().includes(query) : true))
        .slice(0, 8);
    },
    [tagOptions, tagInputByKey]
  );

  useEffect(() => {
    if (openCategoryKey == null && openTagMenuKey == null) return undefined;
    const close = () => {
      setOpenCategoryKey(null);
      setCategoryMenuPos(null);
      setOpenTagMenuKey(null);
      setTagMenuPos(null);
    };
    const onScroll = () => close();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [openCategoryKey, openTagMenuKey]);


  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <TrodoLoader size={60} label="Loading events" />
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="rounded-md border border-[color:var(--feedback-error)] bg-danger-soft px-3 py-2 text-[13px] text-danger">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full space-y-3">
      {error && events.length > 0 && (
        <div className="rounded-md border border-[color:var(--feedback-error)] bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</div>
      )}

      <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="overflow-x-auto">
          <table className="min-w-[820px] w-full text-left">
            <caption className="sr-only">Custom events, with their display name, category and tags</caption>
            <thead>
              <tr className={tableHeaderRowClass}>
                <th scope="col" className={`${tableHeaderCellClass} min-w-[180px]`}>Event name</th>
                <th scope="col" className={`${tableHeaderCellClass} min-w-[140px]`}>Display name</th>
                <th scope="col" className={`${tableHeaderCellClass} min-w-[110px]`}>Category</th>
                <th scope="col" className={`${tableHeaderCellClass} min-w-[180px]`}>Tags</th>
                <th scope="col" className={`${tableHeaderCellClass} w-24 !text-right`}>Count</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3.5 py-10 text-center">
                    <p className="text-[13px] font-[450] text-[color:var(--text-primary)]">No custom events yet</p>
                    {/* This page lists custom events only, so "no events" was
                        indistinguishable from "your events aren't custom". */}
                    <p className="mx-auto mt-1 max-w-md text-[11px] leading-snug text-[color:var(--text-muted)]">
                      This page lists the custom events your app sends. Once you track one with the
                      Trodo SDK, it shows up here and you can give it a display name, category and tags.
                    </p>
                  </td>
                </tr>
              ) : (
                events.map((event, index) => {
                  const key = stableEventKey(event);
                  const draft = draftByKey[key] || toDraft(event);
                  const isSaving = savingKey === key;
                  const showCellErr = cellErrorKey === key;

                  return (
                    <tr key={key} className={tableRowClass.replace('cursor-pointer ', '')}>
                      <td className={tableDataCellClass}>
                        <span className="text-[12px] tracking-tight text-[color:var(--text-primary)]">{event.event_name}</span>
                      </td>
                      <td className={`${tableDataCellClass} py-1`}>
                        <InlineTextCell
                          value={draft.display_name}
                          emptyHint="Add Display Name …"
                          onCommit={(next) => commitDisplayName(event, next)}
                          ariaLabel={`Display name for ${event.event_name}`}
                          saving={isSaving}
                          saveError={showCellErr ? 'Could not save' : null}
                        />
                      </td>
                      <td className={`${tableDataCellClass} relative py-1`}>
                        <button
                          type="button"
                          className={`${cellButtonClass} w-full max-w-[200px] justify-between text-[color:var(--text-muted)]`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = openCategoryKey === key ? null : key;
                            setOpenCategoryKey(next);
                            setOpenTagMenuKey(null);
                            setTagMenuPos(null);
                            if (next) {
                              const r = e.currentTarget.getBoundingClientRect();
                              setCategoryMenuPos({
                                top: r.bottom + 4 + window.scrollY,
                                left: r.left + window.scrollX,
                                width: Math.max(r.width, 220)
                              });
                            } else {
                              setCategoryMenuPos(null);
                            }
                          }}
                        >
                          <span className="min-w-0 flex-1 text-left">
                            {draft.category?.trim() ? (
                              <span className="block truncate rounded px-1 py-0.5 text-[color:var(--text-primary)] group-hover:bg-surface-hover/80">
                                {draft.category.trim()}
                              </span>
                            ) : (
                              <span
                                className="pointer-events-none block min-h-[1.25rem] w-full truncate rounded px-1 py-0.5 text-[12px] text-[color:var(--text-disabled)] transition-colors group-hover:bg-surface-hover/80 group-hover:text-[color:var(--text-muted)]"
                                aria-hidden
                              >
                                Add category…
                              </span>
                            )}
                          </span>
                          <ChevronDown className="ml-1 h-4 w-4 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
                        </button>
                        {openCategoryKey === key && categoryMenuPos &&
                          createPortal(
                            <>
                              <div
                                className="fixed inset-0 z-[1390]"
                                onClick={() => {
                                  setOpenCategoryKey(null);
                                  setCategoryMenuPos(null);
                                }}
                              />
                              <div
                                className="fixed z-[1400] max-h-64 space-y-2 overflow-auto rounded-lg border border-line bg-surface-raised p-2 shadow-token-lg"
                                style={{
                                  top: categoryMenuPos.top,
                                  left: categoryMenuPos.left,
                                  width: categoryMenuPos.width
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="max-h-44 overflow-auto space-y-1">
                                  {categoryOptions.map((cat) => (
                                    <button
                                      key={cat}
                                      type="button"
                                      onClick={() => selectCategory(event, cat)}
                                      className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[13px] text-[color:var(--text-primary)] hover:bg-surface-sunken"
                                    >
                                      {cat}
                                      {draft.category === cat ? (
                                        <Check size={14} className="text-[color:var(--brand-primary)]" />
                                      ) : null}
                                    </button>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenCategoryKey(null);
                                    setCategoryMenuPos(null);
                                    setIsCreatingCategoryByKey((prev) => ({ ...prev, [key]: true }));
                                    setNewCategoryByKey((prev) => ({ ...prev, [key]: draft.category || '' }));
                                  }}
                                  className="w-full rounded-md px-3 py-2 text-left text-[13px] hover:bg-surface-sunken text-[color:var(--brand-primary)]"
                                >
                                  + Create category
                                </button>
                              </div>
                            </>,
                            document.body
                          )}
                        {isCreatingCategoryByKey[key] && (
                          <div className="mt-2 max-w-[220px]">
                            <input
                              type="text"
                              value={newCategoryByKey[key] || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setNewCategoryByKey((prev) => ({ ...prev, [key]: v }));
                                patchDraft(event, { category: v });
                                scheduleAutosave(key);
                              }}
                              onBlur={() => {
                                setIsCreatingCategoryByKey((prev) => ({ ...prev, [key]: false }));
                              }}
                              className={INPUT_CLS}
                              placeholder="Type category"
                            />
                          </div>
                        )}
                      </td>
                      <td className={`${tableDataCellClass} group/tagcell relative py-1`}>
                        {normalizeTags(draft.tags).length === 0 && (
                          <span
                            className="pointer-events-none absolute left-3 top-2 z-0 text-[12px] text-[color:var(--text-disabled)] transition-colors group-hover/tagcell:text-[color:var(--text-muted)]"
                            aria-hidden
                          >
                            Add tags …
                          </span>
                        )}
                        <div className="relative z-10 flex flex-wrap items-center gap-1 min-w-[10rem] max-w-sm min-h-[1.5rem]">
                          {normalizeTags(draft.tags).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 rounded-md border border-line bg-surface-sunken px-2 py-0.5 text-[11px] text-[color:var(--text-primary)]"
                            >
                              <span className="max-w-[120px] truncate">{tag}</span>
                              <button
                                type="button"
                                onClick={() => removeTag(event, tag)}
                                className="p-0.5 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                                aria-label={`Remove ${tag}`}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                          <button
                            type="button"
                            title="Add tags"
                            className="inline-flex items-center gap-1 rounded-md border border-dashed border-line px-2 py-1 text-[11px] text-[color:var(--text-muted)] hover:bg-surface-sunken"
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = openTagMenuKey === key ? null : key;
                              setOpenTagMenuKey(next);
                              setOpenCategoryKey(null);
                              setCategoryMenuPos(null);
                              if (next) {
                                const r = e.currentTarget.getBoundingClientRect();
                                setTagMenuPos({
                                  top: r.bottom + 4 + window.scrollY,
                                  left: r.left + window.scrollX,
                                  width: Math.max(r.width, 260)
                                });
                                setTagInputByKey((prev) => ({ ...prev, [key]: '' }));
                              } else {
                                setTagMenuPos(null);
                              }
                            }}
                          >
                            <Plus size={12} />
                            {normalizeTags(draft.tags).length > 0 ? <span>Add</span> : null}
                          </button>
                        </div>
                        {openTagMenuKey === key && tagMenuPos &&
                          createPortal(
                            <>
                              <div
                                className="fixed inset-0 z-[1390]"
                                onClick={() => {
                                  setOpenTagMenuKey(null);
                                  setTagMenuPos(null);
                                }}
                              />
                              <div
                                className="fixed z-[1400] space-y-2 rounded-lg border border-line bg-surface-raised p-2 shadow-token-lg"
                                style={{
                                  top: tagMenuPos.top,
                                  left: tagMenuPos.left,
                                  width: tagMenuPos.width
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  value={tagInputByKey[key] || ''}
                                  onChange={(e) => setTagInputByKey((prev) => ({ ...prev, [key]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      addTag(event, tagInputByKey[key]);
                                    }
                                  }}
                                  className={INPUT_CLS}
                                  placeholder="Search or create tag"
                                />
                                <div className="max-h-36 overflow-auto space-y-1">
                                  {getAvailableTagOptions(key).map((tag) => (
                                    <button
                                      key={tag}
                                      type="button"
                                      onClick={() => addTag(event, tag)}
                                      className="w-full rounded-md px-3 py-2 text-left text-[13px] text-[color:var(--text-primary)] hover:bg-surface-sunken"
                                    >
                                      {tag}
                                    </button>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    addTag(event, tagInputByKey[key]);
                                    setOpenTagMenuKey(null);
                                    setTagMenuPos(null);
                                  }}
                                  className="rounded-md px-2 py-1 text-[11px] font-medium hover:bg-surface-sunken text-[color:var(--brand-primary)]"
                                >
                                  Add & close
                                </button>
                              </div>
                            </>,
                            document.body
                          )}
                        {isSaving && (
                          <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">Saving…</div>
                        )}
                      </td>
                      <td className={`${tableDataCellClass} !text-right`}>
                        <span className="tabular-nums text-[12px] text-[color:var(--text-muted)]">
                          {Number(event.count).toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CipherEvents;
